import { IStaffAvailableItem } from 'services/shop/types/staff';
import StaffListItemExpanded from './StaffListItemExpanded';
import styled from 'styled-components';
import ModalConfirm, { useModalConfirmRef } from 'components/Modal/ModalConfirm';
import userApis from 'features/user/services/apis';
import { EStaffListStatus } from 'features/user/services/constants';
import { getMessageError, IResponseDataBody } from 'services/response';
import toast from 'utils/toast';
import { useAppDispatch } from 'store/hooks';
import userActions from 'features/user/services/actions';
import { useEffect, useState } from 'react';
import {
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  PointerSensor,
  pointerWithin,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';

interface StaffListExpandedProps {
  data: IStaffAvailableItem[];
  staffClick: (item: IStaffAvailableItem) => () => void;
  onVoiceCall: (staff: IStaffAvailableItem) => void;
}

const StaffListExpanded = ({ data, staffClick, onVoiceCall }: StaffListExpandedProps) => {
  const [items, setItems] = useState<IStaffAvailableItem[]>([]);
  const [activeStaff, setActiveStaff] = useState<IStaffAvailableItem | null>(null);

  useEffect(() => {
    setItems(data);
  }, [data]);

  const confirmModalRef = useModalConfirmRef();
  const dispatch = useAppDispatch();

  const handleChangeStatus = (staff: IStaffAvailableItem) => {
    confirmModalRef.current?.open(
      `The staff will immediately become ${staff.statusWorking === EStaffListStatus.AVAILABLE ? 'Busy and will no longer receive the next customer.' : 'Available and can receive the next customer.'}`,
      () => {
        updateStaffStatus(staff);
      },
      `Mark Staff as ${staff.statusWorking === EStaffListStatus.AVAILABLE ? 'Busy' : 'Available'}?`,
    );
  };

  const updateStaffStatus = async (staff: IStaffAvailableItem) => {
    try {
      const res: IResponseDataBody<boolean> = await userApis.updateStatusWorking(
        staff.staffId,
        staff.statusWorking === EStaffListStatus.AVAILABLE ? EStaffListStatus.BUSY : EStaffListStatus.AVAILABLE,
      );

      if (res?.data?.data) {
        toast.success('Staff status updated successfully');
        dispatch(userActions.getListAvailableWorking.fetch());
      }
    } catch (error) {
      toast.error(getMessageError(error) || 'Failed to update staff status');
    }
  };

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
  );

  const handleDragStart = (event: DragStartEvent) => {
    const staff = event.active.data.current?.staff as IStaffAvailableItem;

    if (!staff) return;

    setActiveStaff(staff);
  };

  const handleDragCancel = () => {
    setActiveStaff(null);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (!over) return;

    const activeId = active.id;
    const overId = over.id;

    if (activeId !== overId) {
      const oldIndex = items.findIndex((item) => item.staffId === activeId);
      const newIndex = items.findIndex((item) => item.staffId === overId);

      const newItems = [...items];
      const [movedItem] = newItems.splice(oldIndex, 1);
      newItems.splice(newIndex, 0, movedItem);
      handleUpdateOrder(newItems);
      setItems(newItems);
    }
  };

  const handleUpdateOrder = async (newItems: IStaffAvailableItem[]) => {
    const ids = newItems.map((o) => o.staffId);
    try {
      const resSort: IResponseDataBody<boolean> = await userApis.sortCheckIn(ids);
      if (resSort.data.data) {
        toast.success('Staff order updated successfully');
      }
    } catch (error) {
      toast.error(getMessageError(error) || 'Failed to update staff order');
      setItems(data);
    }
  };

  return (
    <Container>
      <DndContext
        onDragStart={handleDragStart}
        collisionDetection={pointerWithin}
        onDragEnd={handleDragEnd}
        onDragCancel={handleDragCancel}
        sensors={sensors}
      >
        <SortableContext items={items.map((i) => i.staffId)} strategy={verticalListSortingStrategy}>
          {items.map((o) => (
            <StaffListItemExpanded
              key={o.staffId}
              onVoiceCall={() => onVoiceCall(o)}
              data={o}
              onChangeStatus={() => handleChangeStatus(o)}
              staffClick={staffClick(o)}
            />
          ))}
        </SortableContext>
        <DragOverlay dropAnimation={null}>
          {activeStaff ? <StaffListItemExpanded data={activeStaff} /> : null}
        </DragOverlay>
      </DndContext>
      <ModalConfirm ref={confirmModalRef} />
    </Container>
  );
};

export default StaffListExpanded;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;
